// Small pictures for the Discord guide, drawn in CSS to look like what TOGΞ really posts. The wording and layout
// follow the cogs in the togebot repo (the file is named on each one). Names and numbers are only examples, and each
// picture says so, apart from the Togepi pet, which shows its real stats. As in the rest of the guide, sync intervals
// aren't mentioned, so they're left out of the footers.

import { createContext, useContext } from 'react';

// TOGΞ's real avatar, from the live server stats the guide already loads
export const BotAvatar = createContext(null);
// The server Togepi's real stats, as the togepi cog syncs them for the Arcade (null until they load)
export const LivePet = createContext(null);

const BRAND = 'CHΞΞKSCORD';
const WHATNOT = '#fbe510';

export const SectionHeading = ({ children }) => (
  <h2 className="flex items-center gap-2 font-bold uppercase text-white mt-3" style={{ fontSize: '0.75rem', letterSpacing: '0.05em', lineHeight: 1 }}>
    <span className="rounded-full flex-shrink-0" style={{ width: '4px', height: '14px', background: 'linear-gradient(180deg,#04aff7,#f42ae4)' }} aria-hidden="true" />
    {children}
  </h2>
);

const Button = ({ color = '#4e5058', children }) => (
  <span className="inline-flex items-center gap-1 rounded font-medium text-white whitespace-nowrap" style={{ fontSize: '0.62rem', lineHeight: 1, padding: '5px 7px', background: color }}>{children}</span>
);

const WhatnotIcon = () => <img src="/wnlogo.png" alt="" className="object-contain" style={{ width: '11px', height: '11px' }} />;

// A message from TOGΞ: its name above whatever it posted
const Message = ({ label, note = 'Example', children }) => {
  const avatar = useContext(BotAvatar);
  return (
    <div className="relative rounded-xl p-2.5 min-w-0" style={{ background: '#313338' }} aria-label={`${note}: ${label}`} role="img">
      <div className="flex items-center gap-1.5 mb-1.5">
        <img src={avatar || '/pball.svg'} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: '18px', height: '18px' }} />
        <span className="text-white font-semibold" style={{ fontSize: '0.68rem', lineHeight: 1 }}>TOGΞ</span>
        <span className="font-bold text-white" style={{ fontSize: '0.48rem', lineHeight: 1, padding: '2px 3px', borderRadius: '3px', background: '#5865f2' }}>APP</span>
        <span className="ml-auto text-zinc-500" style={{ fontSize: '0.56rem', lineHeight: 1 }}>{note}</span>
      </div>
      {children}
    </div>
  );
};

const Embed = ({ color, children }) => (
  <div className="rounded p-2 flex flex-col gap-1.5 min-w-0" style={{ background: '#2b2d31', borderLeft: `3px solid ${color}` }}>{children}</div>
);

const EmbedTitle = ({ children }) => <div className="text-white font-semibold" style={{ fontSize: '0.72rem', lineHeight: 1.2 }}>{children}</div>;
const Footer = ({ children }) => <div className="text-zinc-400" style={{ fontSize: '0.56rem', lineHeight: 1.2 }}>{children}</div>;

const Field = ({ name, value }) => (
  <div className="min-w-0">
    <div className="text-zinc-300 font-semibold truncate" style={{ fontSize: '0.58rem', lineHeight: 1.2 }}>{name}</div>
    <div className="text-white" style={{ fontSize: '0.66rem', lineHeight: 1.3 }}>{value}</div>
  </div>
);

// whatnotlive: the live alert
function LiveAlert() {
  return (
    <Message label="a Whatnot live alert">
      <Embed color={WHATNOT}>
        <div className="text-zinc-300 font-medium" style={{ fontSize: '0.6rem', lineHeight: 1 }}>@togecheeks</div>
        <EmbedTitle>🔴 cheeks is LIVE on Whatnot!</EmbedTitle>
        <div className="text-white font-semibold" style={{ fontSize: '0.64rem', lineHeight: 1.2 }}>Pokémon singles and slabs</div>
        <div className="grid grid-cols-2 gap-1.5">
          <Field name="👀 Current views" value="128" />
          <Field name="📈 Max views" value="186" />
          <Field name="⏱️ Live for" value="1h 12m" />
          <Field name="👥 Followers" value="5,064" />
        </div>
        {/* The same thumbnail as the Whatnot alert in TogeAdvert.jsx */}
        <div className="relative flex items-center justify-center overflow-hidden rounded" style={{ aspectRatio: '16 / 8', background: 'radial-gradient(circle at 30% 120%, rgba(255,255,255,0.25), transparent 55%), repeating-linear-gradient(115deg, rgba(255,255,255,0.05) 0 6px, transparent 6px 12px), linear-gradient(135deg, #2a2400 0%, #8a7a00 50%, #fbe510 100%)' }}>
          <img src="/wnlogo.png" alt="" className="object-contain" style={{ width: '42px', height: '42px', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }} />
          <span className="absolute text-white font-extrabold rounded-sm" style={{ top: '6px', left: '6px', fontSize: '0.56rem', lineHeight: 1, padding: '2px 5px', letterSpacing: '0.05em', background: '#e91916' }}>● LIVE</span>
          <span className="absolute text-white font-bold rounded-sm" style={{ bottom: '6px', left: '6px', fontSize: '0.56rem', lineHeight: 1, padding: '2px 5px', background: 'rgba(0,0,0,0.6)' }}>👁 128</span>
        </div>
        <Footer>{BRAND} • 📸 Live snapshot</Footer>
      </Embed>
      <div className="mt-1.5"><Button><WhatnotIcon />Watch on Whatnot</Button></div>
    </Message>
  );
}

// wnprofile: a real ?profile card, drawn by the bot's own card.py for @togecheeks. The numbers are as of when it was
// drawn, so it wants redrawing now and then
function ProfileCard() {
  return (
    <Message label="a real Whatnot profile card" note="Real card">
      <img src="/guide/profile-card.webp" width={1200} height={620} alt="" loading="lazy" className="block w-full h-auto rounded-lg" />
    </Message>
  );
}

function WhatnotVisuals() {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-2"><LiveAlert /><ProfileCard /></div>;
}

// The restock alerts come from the separate monitor, not this repo: this shows the idea, not its exact layout
function RestockAlert() {
  return (
    <Message label="a restock alert">
      <div className="mb-1.5" style={{ fontSize: '0.64rem', lineHeight: 1.2 }}>
        <span className="rounded font-medium" style={{ padding: '0 3px', background: 'rgba(88,101,242,0.3)', color: '#c9cdfb' }}>@Destined Rivals</span>
      </div>
      <Embed color="#23a55a">
        <EmbedTitle>🔔 Back in stock at Argos</EmbedTitle>
        <div className="text-zinc-200" style={{ fontSize: '0.66rem', lineHeight: 1.3 }}>Destined Rivals Elite Trainer Box</div>
      </Embed>
      <div className="mt-1.5"><Button color="#248046">🛒 Go to product</Button></div>
    </Message>
  );
}

// buttonticket: the DM when a ticket closes
function TicketClosed() {
  return (
    <Message label="a ticket closed message">
      <Embed color="#da373c">
        <EmbedTitle>Ticket Closed</EmbedTitle>
        <div className="text-zinc-200" style={{ fontSize: '0.64rem', lineHeight: 1.3 }}>Your ticket in <b>CHΞΞKSCORD</b> has been closed.</div>
        <div className="text-zinc-200" style={{ fontSize: '0.64rem', lineHeight: 1.3 }}><b>Reason:</b> All sorted, thanks!</div>
        <Footer>{BRAND} • Ticket ID: #1042</Footer>
      </Embed>
    </Message>
  );
}

// cert_lookup: pick the grader, type the number, get a private link
function CertSearch() {
  return (
    <Message label="a slab cert search">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between rounded text-zinc-200" style={{ fontSize: '0.64rem', lineHeight: 1, padding: '6px 8px', background: '#1e1f22' }}>PSA<span className="text-zinc-400" aria-hidden="true">▾</span></div>
        <div className="rounded p-2" style={{ background: '#2b2d31' }}>
          <div className="text-white font-semibold" style={{ fontSize: '0.68rem', lineHeight: 1 }}>PSA Cert Search</div>
          <div className="uppercase font-bold text-zinc-300 mt-1.5" style={{ fontSize: '0.5rem', letterSpacing: '0.03em', lineHeight: 1 }}>Certificate Number</div>
          <div className="rounded mt-1 text-white" style={{ fontSize: '0.64rem', lineHeight: 1, padding: '5px 6px', background: '#1e1f22' }}>12345678</div>
        </div>
        <div className="font-medium" style={{ fontSize: '0.66rem', lineHeight: 1.2, color: '#00a8fc' }}>PSA cert 12345678</div>
      </div>
    </Message>
  );
}

// giveaway: the post, in its pink
function Giveaway() {
  return (
    <Message label="a giveaway post">
      <Embed color="#ff73fa">
        <div className="text-white font-bold" style={{ fontSize: '0.8rem', lineHeight: 1.2 }}>Destined Rivals Booster Bundle</div>
        <div className="text-zinc-200" style={{ fontSize: '0.64rem', lineHeight: 1.2 }}>Ends in 2 days</div>
        <Footer>{BRAND} · Entries: 128</Footer>
      </Embed>
    </Message>
  );
}

// Low bars turn red, as in the Arcade (the cog counts under 30 as needing help)
const Stat = ({ icon, name, value }) => {
  const v = Math.max(0, Math.min(100, Math.round(value ?? 0)));
  return (
    <div className="flex items-center gap-1.5" style={{ fontSize: '0.6rem', lineHeight: 1 }}>
      <span className="flex-shrink-0" aria-hidden="true">{icon}</span>
      <span className="text-zinc-300 flex-shrink-0" style={{ width: '36px' }}>{name}</span>
      <span className="flex-1 rounded-full overflow-hidden" style={{ height: '6px', background: '#1e1f22' }}><span className="block h-full rounded-full transition-all duration-700" style={{ width: `${v}%`, background: v < 30 ? '#ef4444' : 'linear-gradient(90deg,#04aff7,#f42ae4)' }} /></span>
      <span className="text-right flex-shrink-0" style={{ width: '24px', color: v < 30 ? '#f87171' : '#d4d4d8' }}>{v}%</span>
    </div>
  );
};

// togepi: the shared pet, with its real name, level, mood and stats (the same ones as the Arcade), and its buttons.
// Until they load, or if they can't, it falls back to example numbers and says so
const EXAMPLE_PET = { species: 'Togepi', level: 1, food: 82, happiness: 95, energy: 64, clean: 71 };

function Pet() {
  const live = useContext(LivePet);
  const p = live || EXAMPLE_PET;
  const name = p.nickname ? `${p.nickname} the ${p.species}` : p.species;
  return (
    <Message label={`the server's ${p.species}`} note={live ? '● Live' : 'Example'}>
      <div className="flex items-center gap-2.5">
        <img src={p.sprite || '/togepi.png'} alt="" loading="lazy" className="object-contain flex-shrink-0" style={{ width: '58px', height: '58px', imageRendering: p.sprite ? 'pixelated' : undefined }} />
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0" style={{ lineHeight: 1.2 }}>
            <span className="text-white font-semibold truncate" style={{ fontSize: '0.7rem' }}>{name}</span>
            {p.level ? <span className="flex-shrink-0 rounded font-bold text-white" style={{ fontSize: '0.5rem', padding: '1px 4px', background: 'linear-gradient(135deg,#04aff7,#f42ae4)' }}>Lv. {p.level}</span> : null}
          </div>
          {p.mood && <span className="text-zinc-400 truncate" style={{ fontSize: '0.58rem', lineHeight: 1.2 }}>{p.mood}</span>}
          <Stat icon="🍖" name="Food" value={p.food} />
          <Stat icon="💛" name="Happy" value={p.happiness} />
          <Stat icon="⚡" name="Energy" value={p.energy} />
          <Stat icon="🛁" name="Clean" value={p.clean} />
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        <Button color="#248046">🍖 Feed</Button>
        <Button color="#5865f2">🎾 Play</Button>
        <Button>🤗 Pet</Button>
        <Button color="#5865f2">🛁 Bath</Button>
        <Button>💤 Nap</Button>
      </div>
    </Message>
  );
}

// hangman: the board, part-way through a round
function Hangman() {
  return (
    <Message label="a game of Hangman">
      <Embed color="#3498db">
        <EmbedTitle>🎮 Hangman</EmbedTitle>
        <div className="text-zinc-200" style={{ fontSize: '0.62rem', lineHeight: 1.2 }}><b>Hint:</b> Gen I Pokémon · 7 letters</div>
        <code className="block rounded text-center font-bold" style={{ fontSize: '0.8rem', letterSpacing: '0.12em', lineHeight: 1, padding: '7px 4px', background: '#1e1f22', color: '#f0b232' }}>P _ K A _ _ U</code>
        <div className="grid grid-cols-2 gap-1.5">
          <Field name="Lives" value="❤️❤️❤️❤️🖤🖤" />
          <Field name="Wrong guesses" value="S T" />
        </div>
        <div className="text-zinc-200" style={{ fontSize: '0.62rem', lineHeight: 1.2 }}>🥇 <b>Ash</b> — 3 pts</div>
      </Embed>
    </Message>
  );
}

export const VISUALS = {
  whatnot: WhatnotVisuals,
  restock: RestockAlert,
  ticket: TicketClosed,
  cert: CertSearch,
  giveaway: Giveaway,
  pet: Pet,
  hangman: Hangman,
};
